import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type Oficio, type OficinaFicha, type OficinaMaterial, type OficinaMenu, type OficinaReceita } from '../api/types';
import { playAlfaiataria, playArcos, playForja } from '../audio/mixer';
import { prefetchIcons } from '../assets/items';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import { nomeDoItem, nomeDoMaterial } from '../i18n/nomes';
import { CraftBar } from './CraftBar';
import { FaixaOficio, garantirPerto } from './FaixaOficio';
import { FletchGump } from './FletchGump';
import { ItemIcon } from './ItemIcon';
import { useModo } from './hud/useModoTela';
import { TailorGump } from './TailorGump';
import { definirCraft } from './craftAndamento';

/**
 * A oficina de armas e armaduras (techspec-craft): Ferreiro (lingotes) e Arqueiro (toras) na mesma janela.
 * Escolhe o material em cima, a peça na lista e faz na ficha. Qualidade e nome saem do servidor;
 * o cliente só mostra o normal e o excepcional com os números que o menu manda.
 */

export type ResultadoOficina =
  | { evento: 'fez'; nome: string; excepcional: boolean }
  | { evento: 'municao'; texto: string };

interface Props {
  oficio: Oficio;
  active: boolean;
  tick: number;
  onResultado(ev: ResultadoOficina): void;
  onVerMochila?(): void;
}

/** A linha extra do fim da lista: a munição no Arcos, as bandagens na Alfaiataria. */
const EXTRA = '__extra';

/** Os textos de cada oficina (os códigos do servidor: OficinaCodes). */
const TXT: Record<Oficio, {
  titulo: string; pericia: string; icone: string; naoFez: string; fazer: string; fazendo: string;
  periciaMaterial: string; periciaBaixa: string; semFerramenta: string; semMaterial: string; insumo: string; secArmadura: string;
}> = {
  ferraria: {
    titulo: 'ferraria.titulo', pericia: 'skill.blacksmithing', icone: 'iron-ingot', naoFez: 'ferraria.naoForjou',
    fazer: 'ferraria.forjar', fazendo: 'ferraria.forjando', periciaMaterial: 'ferraria.periciaMetal',
    periciaBaixa: 'ferraria.periciaBaixa', semFerramenta: '', semMaterial: 'ferraria.semLingote', insumo: 'oficina.lingotes',
    secArmadura: 'oficina.secPlacas'
  },
  arcos: {
    titulo: 'arcos.titulo', pericia: 'skill.fletching', icone: 'log', naoFez: 'arcos.naoEntalhou',
    fazer: 'oficina.fazer', fazendo: 'oficina.fazendo', periciaMaterial: 'arcos.periciaMadeira',
    periciaBaixa: 'arcos.periciaBaixa', semFerramenta: 'arcos.semFerramenta', semMaterial: 'arcos.semMaterial', insumo: 'oficina.toras',
    secArmadura: 'oficina.secPlacas'
  },
  alfaiataria: {
    titulo: 'alfaiataria.titulo', pericia: 'skill.tailoring', icone: 'leather', naoFez: 'alfaiataria.naoCosturou',
    fazer: 'alfaiataria.costurar', fazendo: 'alfaiataria.costurando', periciaMaterial: 'alfaiataria.periciaCouro',
    periciaBaixa: 'alfaiataria.periciaBaixa', semFerramenta: 'alfaiataria.semKit', semMaterial: 'alfaiataria.semCouro',
    insumo: 'oficina.couros', secArmadura: 'oficina.secCouro'
  }
};

interface Feito {
  readonly oficio: Oficio;
  readonly defId: string;
  readonly nome: string;
  readonly qualidade: number;
  readonly feitoPor: string;
  readonly valor: string;
  readonly bonus: number;
  /** No lote: quantas saíram e quantas excepcionais (o card mostra a última). */
  readonly feitas?: number;
  readonly excepcionais?: number;
}

/** Peças por vez na oficina: cada uma ainda é um pedido, no tempo dela. */
const LOTE_MAX = 100;

export function OficinaGump({ oficio, active, tick, onResultado, onVerMochila }: Props) {
  const modo = useModo();
  const cel = modo === 'cel';
  const [menus, setMenus] = useState<Partial<Record<Oficio, OficinaMenu>>>({});
  const [mat, setMat] = useState<Partial<Record<Oficio, string>>>({});
  const [sel, setSel] = useState<Partial<Record<Oficio, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preparo, setPreparo] = useState<{ ate: number; ms: number } | null>(null);
  const [feito, setFeito] = useState<Feito | null>(null);
  const [celFicha, setCelFicha] = useState(false);
  const timer = useRef<number | null>(null);
  const somTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current != null) window.clearTimeout(timer.current);
    if (somTimer.current != null) window.clearInterval(somTimer.current);
  }, []);

  useEffect(() => {
    if (!active) return;
    let live = true;
    void api.oficinaMenu(oficio)
      .then(next => {
        if (!live) return;
        setMenus(m => ({ ...m, [oficio]: next }));
        setError(null);
        prefetchIcons([
          ...next.materiais.map(m => m.insumo),
          ...next.receitas.flatMap(r => r.fichas.map(f => f.defId))
        ]);
      })
      .catch(err => {
        if (!live) return;
        setError(err instanceof ApiError ? err.message : t(TXT[oficio].naoFez));
      });
    return () => { live = false; };
  }, [active, tick, oficio]);

  useEffect(() => { setCelFicha(false); setError(null); }, [oficio]);

  const menu = menus[oficio] ?? null;
  const material = escolherMaterial(menu, mat[oficio]);
  const selId = sel[oficio] ?? menu?.receitas[0]?.defId ?? null;
  const temExtra = oficio === 'arcos' || oficio === 'alfaiataria';
  const municao = temExtra && selId === EXTRA;
  const receita = municao ? null : menu?.receitas.find(r => r.defId === selId) ?? menu?.receitas[0] ?? null;

  /**
   * Fazer várias de uma vez (Renato, 04/11/2026): uma peça por pedido, cada uma no tempo de sempre, até acabar a conta,
   * faltar material ou o jogador parar. O servidor confere cada uma (perícia, material, ferramenta, ritmo); o som do
   * ofício toca enquanto a fila anda. O resultado mostra a última peça e o total.
   */
  const pararLote = useRef(false);
  const [lote, setLote] = useState<{ feitas: number; total: number } | null>(null);
  const fazer = async (total: number) => {
    if (!menu || !material || !receita) return;
    setBusy(true);
    setError(null);
    setFeito(null);
    pararLote.current = false;
    const som = oficio === 'arcos' ? playArcos : oficio === 'alfaiataria' ? playAlfaiataria : playForja;
    let ultimo: Feito | null = null;
    let feitas = 0, excepcionais = 0;
    const ficha0 = fichaDe(receita, material);
    const nome0 = nomeDoItem(ficha0?.defId ?? receita.defId, ficha0?.nome ?? receita.nome);
    const andamento = (fase: 'indo' | 'fazendo', feitasAgora: number) => definirCraft({
      oficio, nome: nome0, defId: ficha0?.defId ?? receita.defId, fase, feitas: feitasAgora, total, inicio
    }, () => { pararLote.current = true; });
    const inicio = Date.now();
    const fim = (erro?: string) => {
      if (somTimer.current != null) { window.clearInterval(somTimer.current); somTimer.current = null; }
      definirCraft(null);
      setPreparo(null);
      setBusy(false);
      setLote(null);
      if (erro) setError(erro);
      if (ultimo) setFeito({ ...ultimo, feitas, excepcionais });
    };
    try {
      // Na cidade, longe da ferramenta: anda até ela e começa ao chegar. Na casa crafta de onde estiver.
      // Primeiro chega; só então o som e a primeira peça (o servidor confirma que está perto).
      andamento('indo', 0);
      const perto = await garantirPerto(oficio);
      if (!perto.ok) { fim(perto.motivo); return; }
      if (pararLote.current) { fim(); return; }
      // O som não para entre uma peça e outra: toca de novo a cada 1,7 s enquanto a fila anda.
      som();
      somTimer.current = window.setInterval(som, 1700);
      for (let i = 0; i < total; i++) {
        if (pararLote.current) break;
        setLote({ feitas: i, total });
        andamento('fazendo', i);
        const res = await api.oficinaFazer(oficio, receita.defId, material.id);
        const ficha = fichaDe(receita, material);
        const nome = nomeDoItem(res.defId, ficha?.nome);
        const bonus = res.qualidade === 2 ? receita.excepcional : receita.bonus;
        setPreparo({ ate: performance.now() + res.prontoEmMs, ms: res.prontoEmMs });
        await new Promise<void>(ok => { timer.current = window.setTimeout(() => { timer.current = null; ok(); }, res.prontoEmMs); });
        feitas++;
        if (res.qualidade === 2) excepcionais++;
        ultimo = {
          oficio, defId: res.defId, nome, qualidade: res.qualidade, feitoPor: res.feitoPor, bonus,
          valor: ficha ? textoValor(ficha, material.forca, bonus) : ''
        };
        onResultado({ evento: 'fez', nome, excepcional: res.qualidade === 2 });
      }
      fim();
    } catch (err) {
      fim(err instanceof ApiError ? err.message : t(TXT[oficio].naoFez));
    }
  };

  const escolherPeca = (id: string) => {
    setSel(s => ({ ...s, [oficio]: id }));
    setFeito(null);
    if (cel) setCelFicha(true);
  };

  const lista = (
    <div className="p-of__lista" role="listbox" aria-label={t('oficina.pecas')}>
      {!menu && <p className="p-of-nota">{t('comum.montando')}</p>}
      {menu && material && secoes(menu.receitas).map(([grupo, rs]) => (
        <div key={grupo} role="group" aria-label={t(rotuloSecao(oficio, grupo))}>
          <div className="p-of-sec">{t(rotuloSecao(oficio, grupo))}</div>
          {rs.map(r => {
            const st = estadoLinha(menu, r, material);
            const ficha = fichaDe(r, material);
            const id = ficha?.defId ?? r.defId;
            const nome = nomeDoItem(id, ficha?.nome ?? r.nome);
            return (
              <button
                key={r.defId}
                type="button"
                role="option"
                aria-selected={r.defId === receita?.defId && !municao}
                className={`p-of-l${st.k === 'trava' ? ' is-trava' : ''}`}
                onClick={() => escolherPeca(r.defId)}
              >
                <ItemIcon id={id} label={nome} size={36} eager />
                <span><b>{nome}</b><small>{qtdInsumo(oficio, r.qtd)}</small></span>
                <span className={`p-of-st is-${st.k}`}>{st.txt}</span>
              </button>
            );
          })}
        </div>
      ))}
      {menu && oficio === 'arcos' && (
        <div role="group" aria-label={t('oficina.secMunicao')}>
          <div className="p-of-sec">{t('oficina.secMunicao')}</div>
          <button
            type="button"
            role="option"
            aria-selected={municao}
            className="p-of-l"
            onClick={() => escolherPeca(EXTRA)}
          >
            <ItemIcon id="arrow" label={t('oficina.municao')} size={36} eager />
            <span><b>{t('oficina.municao')}</b><small>{t('oficina.municaoMeta')}</small></span>
            <span className="p-of-st" />
          </button>
        </div>
      )}
      {menu && oficio === 'alfaiataria' && (
        <div role="group" aria-label={t('oficina.secBandagens')}>
          <div className="p-of-sec">{t('oficina.secBandagens')}</div>
          <button type="button" role="option" aria-selected={municao} className="p-of-l" onClick={() => escolherPeca(EXTRA)}>
            <ItemIcon id="bandage" label={nomeDoItem('bandage')} size={36} eager />
            <span><b>{nomeDoItem('bandage')}</b><small>{t('oficina.bandagensMeta')}</small></span>
            <span className="p-of-st" />
          </button>
        </div>
      )}
    </div>
  );

  const ficha = municao && oficio === 'alfaiataria' ? (
    <div className="p-of__ficha p-of__ficha--municao">
      {cel && <button type="button" className="btn btn--tiny p-of-volta" onClick={() => setCelFicha(false)}>‹ {t('oficina.pecas')}</button>}
      <TailorGump
        active={active && municao}
        tick={tick}
        onResultado={({ evento, nome, n }) => onResultado({
          evento: 'municao',
          texto: evento === 'cortou' ? t('alfaiataria.cortou', { n: String(n ?? 5) }) : t('alfaiataria.costurou', { item: nome ?? '' })
        })}
      />
    </div>
  ) : municao ? (
    <div className="p-of__ficha p-of__ficha--municao">
      {cel && <button type="button" className="btn btn--tiny p-of-volta" onClick={() => setCelFicha(false)}>‹ {t('oficina.pecas')}</button>}
      <FletchGump
        active={active && municao}
        tick={tick}
        onResultado={({ evento, nome, n }) => {
          onResultado({
            evento: 'municao',
            texto: evento === 'empenou'
              ? t('arcos.empenou', { n: String(n ?? 10), item: nome ?? '' })
              : t('arcos.entalhou', { item: nome ?? '' })
          });
        }}
      />
    </div>
  ) : menu && material && receita ? (
    <FichaDaPeca
      menu={menu}
      receita={receita}
      material={material}
      busy={busy}
      preparo={preparo}
      lote={lote}
      feito={feito?.oficio === oficio ? feito : null}
      cel={cel}
      onVoltar={() => setCelFicha(false)}
      onFazer={n => void fazer(n)}
      onParar={() => { pararLote.current = true; }}
      onOk={() => setFeito(null)}
      onVerMochila={onVerMochila ? () => { setFeito(null); onVerMochila(); } : undefined}
    />
  ) : (
    <div className="p-of__ficha" />
  );

  return (
    <>
      <div className="p-of__topo">
        <span className="p-of__titulo">
          <ItemIcon id={TXT[oficio].icone} label="" size={20} eager />
          {t(TXT[oficio].titulo)}
        </span>
        <span className="p-of__pericia">
          {t(TXT[oficio].pericia)} <b>{umaCasa(menu?.pericia ?? 0)}</b>
        </span>
      </div>
      <FaixaOficio oficio={oficio} active={active} tick={tick} />
      {error && <p className="notice" role="alert">{error}</p>}
      {!municao && (
        <div className="p-of__mats" role="group" aria-label={t('oficina.material')} style={{ ['--n' as string]: String(menu?.materiais.length ?? 7) }}>
          {(menu?.materiais ?? []).map(m => {
            const trava = !m.pode;
            return (
              <button
                key={m.id}
                type="button"
                className={`p-of-m${trava ? ' is-trava' : ''}`}
                aria-pressed={m.id === material?.id}
                title={nomeDoItem(m.insumo, m.insumoNome)}
                disabled={busy}
                onClick={() => { setMat(s => ({ ...s, [oficio]: m.id })); setFeito(null); }}
              >
                <ItemIcon id={m.insumo} label={nomeDoItem(m.insumo, m.insumoNome)} size={28} eager />
                <b>{nomeDoMaterial(m.id)}</b>
                <small>{trava ? `🔒 ${umaCasa(m.pericia)}` : numero(m.qty)}</small>
              </button>
            );
          })}
        </div>
      )}
      {cel ? (celFicha ? ficha : lista) : <div className="p-of__grade">{lista}{ficha}</div>}
    </>
  );
}

interface FichaProps {
  menu: OficinaMenu;
  receita: OficinaReceita;
  material: OficinaMaterial;
  busy: boolean;
  preparo: { ate: number; ms: number } | null;
  /** A fila de várias peças: quantas já saíram e o total. */
  lote: { feitas: number; total: number } | null;
  feito: Feito | null;
  cel: boolean;
  onVoltar(): void;
  onFazer(qtd: number): void;
  onParar(): void;
  onOk(): void;
  onVerMochila?(): void;
}

function FichaDaPeca({ menu, receita, material, busy, preparo, lote, feito, cel, onVoltar, onFazer, onParar, onOk, onVerMochila }: FichaProps) {
  const txt = TXT[menu.oficio];
  // Quantas dá para fazer com o material da mochila (o servidor confere cada uma). Até 100 por vez.
  const maximo = Math.max(1, Math.min(LOTE_MAX, Math.floor(material.qty / Math.max(1, receita.qtd))));
  const [qtd, setQtd] = useState(1);
  const n = Math.min(qtd, maximo);
  const ficha = fichaDe(receita, material);
  const id = ficha?.defId ?? receita.defId;
  const nome = nomeDoItem(id, ficha?.nome ?? receita.nome);
  const exigida = Math.max(receita.pericia, material.pericia);
  const chance = chanceDe(menu, exigida);
  const motivo = motivoDe(menu, receita, material);
  const linha = (q: 1 | 2) => {
    const bonus = q === 2 ? receita.excepcional : receita.bonus;
    return (
      <div className={`p-of-q${q === 2 ? ' is-exc' : ''}`}>
        <span>{t(q === 2 ? 'oficina.excepcional' : 'oficina.normal')} <small>+{pct(bonus)}</small></span>
        <b>{ficha ? textoValor(ficha, material.forca, bonus) : ''}</b>
        <em>{q === 2 && menu.pericia >= exigida ? t('oficina.chance', { n: pct(chance) }) : ''}</em>
      </div>
    );
  };
  return (
    <div className="p-of__ficha">
      {cel && <button type="button" className="btn btn--tiny p-of-volta" onClick={onVoltar}>‹ {t('oficina.pecas')}</button>}
      <div className="p-of-f__cab">
        <span className="p-of-f__ico"><ItemIcon id={id} label={nome} size={56} eager /></span>
        <div>
          <p className="p-of-f__nome">{nome}</p>
          <p className="p-of-marca">{t('oficina.feitoPor')} <b>{menu.feitoPor}</b></p>
        </div>
      </div>
      <div className="p-of-qs">{linha(1)}{linha(2)}</div>
      <div className="p-of-custo">
        <ItemIcon id={material.insumo} label={nomeDoItem(material.insumo, material.insumoNome)} size={26} eager />
        <span><b>{numero(receita.qtd)}</b> {nomeDoItem(material.insumo, material.insumoNome)}</span>
        <small>{t('oficina.voceTem', { n: numero(material.qty) })}</small>
      </div>
      <div className="p-of-acao">
        {preparo && (
          <CraftBar ateMs={preparo.ate} duracaoMs={preparo.ms}
            rotulo={lote && lote.total > 1 ? `${t(txt.fazendo)} ${t('oficina.loteDe', { n: lote.feitas + 1, total: lote.total })}` : t(txt.fazendo)} />
        )}
        <p className={motivo ? 'is-mau' : undefined}>{motivo}</p>
        {!busy && !motivo && maximo > 1 && (
          <div className="p-of-qtd" role="group" aria-label={t('oficina.quantidade')}>
            <button type="button" className="btn btn--tiny" disabled={n <= 1} onClick={() => setQtd(Math.max(1, n - 1))} aria-label={t('compra.menos')}>−</button>
            <input type="number" min={1} max={maximo} value={n} aria-label={t('oficina.quantidade')}
              onChange={e => setQtd(Math.max(1, Math.min(maximo, Math.floor(Number(e.target.value) || 1))))} />
            <button type="button" className="btn btn--tiny" disabled={n >= maximo} onClick={() => setQtd(n + 1)} aria-label={t('compra.mais')}>+</button>
            <button type="button" className="btn btn--tiny" disabled={n >= maximo} onClick={() => setQtd(maximo)}>{t('oficina.maximo', { n: maximo })}</button>
          </div>
        )}
        {busy && lote && lote.total > 1
          ? <button type="button" className="btn" onClick={onParar}>{t('oficina.parar')}</button>
          : (
            <button type="button" className="btn btn--primary" disabled={busy || !!motivo} onClick={() => onFazer(n)}>
              {n > 1 ? `${t(txt.fazer)} ×${n}` : t(txt.fazer)}
            </button>
          )}
      </div>
      {feito && (
        <div className={`p-of-res${feito.qualidade === 2 ? ' is-exc' : ''}`} role="status">
          <div className="p-of-res__c">
            <span className="p-of-res__ico"><ItemIcon id={feito.defId} label={feito.nome} size={60} eager /></span>
            <p className="p-of-res__n">{feito.nome}</p>
            {(feito.feitas ?? 1) > 1 && (
              <p className="p-of-marca">{t('oficina.loteFeito', { n: feito.feitas ?? 1, exc: feito.excepcionais ?? 0 })}</p>
            )}
            <span className={`rar ${feito.qualidade === 2 ? 'is-exc' : 'is-mao'}`}>
              {t(feito.qualidade === 2 ? 'oficina.excepcional' : 'oficina.normal')} +{pct(feito.bonus)}
            </span>
            {feito.valor && <div className="p-of-res__s"><span>{feito.valor}</span></div>}
            <p className="p-of-marca">{t('oficina.feitoPor')} <b>{feito.feitoPor}</b></p>
            <div className="p-of-res__a">
              {onVerMochila && <button type="button" className="btn btn--tiny" onClick={onVerMochila}>{t('oficina.verMochila')}</button>}
              <button type="button" className="btn btn--tiny btn--primary" onClick={onOk}>{t('oficina.ok')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function rotuloSecao(oficio: Oficio, grupo: string): string {
  if (grupo === 'armor') return TXT[oficio].secArmadura;
  return grupo === 'shield' ? 'oficina.secEscudo' : 'oficina.secArmas';
}

function secoes(receitas: readonly OficinaReceita[]): [string, OficinaReceita[]][] {
  return (['weapon', 'shield', 'armor'] as const)
    .map(g => [g, receitas.filter(r => r.grupo === g)] as [string, OficinaReceita[]])
    .filter(([, rs]) => rs.length > 0);
}

function escolherMaterial(menu: OficinaMenu | null, id: string | undefined): OficinaMaterial | null {
  if (!menu) return null;
  return menu.materiais.find(m => m.id === id) ?? menu.materiais[0] ?? null;
}

function fichaDe(r: OficinaReceita, m: OficinaMaterial): OficinaFicha | undefined {
  return r.fichas.find(f => f.material === m.id);
}

function chanceDe(menu: OficinaMenu, exigida: number): number {
  const r = menu.regras;
  if (menu.pericia < exigida) return 0;
  let c = Math.min(r.chanceTeto, r.chanceBase + r.chancePorPonto * (menu.pericia - exigida));
  if (menu.pericia >= 100) c += r.chanceMestre;
  return Math.max(0, Math.min(100, c));
}

function motivoDe(menu: OficinaMenu, r: OficinaReceita, m: OficinaMaterial): string {
  const txt = TXT[menu.oficio];
  if (!r.ativa) return t('oficina.desligada');
  if (!m.pode) return t(txt.periciaMaterial, { n: umaCasa(m.pericia) });
  if (menu.pericia < r.pericia) return t(txt.periciaBaixa, { n: umaCasa(r.pericia) });
  if (txt.semFerramenta && !menu.temFerramenta) return t(txt.semFerramenta);
  if (m.qty < r.qtd) return t(txt.semMaterial, { n: numero(r.qtd - m.qty) });
  return '';
}

function estadoLinha(menu: OficinaMenu, r: OficinaReceita, m: OficinaMaterial): { k: 'ok' | 'falta' | 'trava'; txt: string } {
  if (!r.ativa) return { k: 'trava', txt: t('oficina.desligadaCurto') };
  const exigida = Math.max(r.pericia, m.pericia);
  if (menu.pericia < exigida) return { k: 'trava', txt: `🔒 ${umaCasa(exigida)}` };
  if (m.qty < r.qtd) return { k: 'falta', txt: t('ferraria.faltam', { n: numero(r.qtd - m.qty) }) };
  return { k: 'ok', txt: '' };
}

function qtdInsumo(oficio: Oficio, n: number): string {
  return t(TXT[oficio].insumo, { n });
}

/** O dado (`4d9+5`) vira a faixa; força do material e bônus da qualidade multiplicam, como no servidor. */
export function textoValor(f: OficinaFicha, forca: number, bonus: number): string {
  const k = (1 + forca / 100) * (1 + bonus / 100);
  const faixa = faixaDoDado(f.dado);
  if (faixa) return t('oficina.dano', { v: `${numero(arred(faixa[0] * k))}–${numero(arred(faixa[1] * k))}` });
  if (f.ar != null) return t('oficina.ar', { v: umaCasa(Math.round(f.ar * k * 10) / 10) });
  return '';
}

function faixaDoDado(dado: string | null | undefined): [number, number] | null {
  const x = /^(\d+)d(\d+)([+-]\d+)?$/.exec((dado ?? '').trim());
  if (!x) return null;
  const n = Number(x[1]), f = Number(x[2]), k = Number(x[3] ?? 0);
  return [n + k, n * f + k];
}

/** Meio para longe do zero, como o `MidpointRounding.AwayFromZero` do servidor. */
function arred(v: number): number {
  return Math.sign(v) * Math.round(Math.abs(v));
}

function umaCasa(n: number): string {
  return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function pct(n: number): string {
  return `${n.toLocaleString(idiomaAtual(), { maximumFractionDigits: 1 })}%`;
}
